import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { resumeConversation, sendChatMessage } from '../services/api'

type UiMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
}

export const useChatStore = defineStore('chat', () => {
  const isOpen = ref(false)
  const isSending = ref(false)
  const isResuming = ref(false)
  const continuityAuthenticated = ref(false)
  const resumeError = ref('')
  const clientName = ref('')
  const professionalName = ref('')
  const conversationId = ref<string | undefined>(sessionStorage.getItem('conversation_id') ?? undefined)
  const greetingMessageId = crypto.randomUUID()

  function greetingFor(name: string): string {
    return name
      ? `Olá. Sou a assistente virtual de ${name}, a Terapeut-IA®. Como posso ajudar?`
      : 'Olá. Sou a Terapeut-IA®, assistente virtual da profissional. Como posso ajudar?'
  }

  const messages = ref<UiMessage[]>([
    {
      id: greetingMessageId,
      role: 'assistant',
      content: greetingFor(''),
    },
  ])

  const canSend = computed(() => !isSending.value && !isResuming.value)

  function setProfessionalName(value: string): void {
    professionalName.value = value.trim()
    const greeting = messages.value.find((message) => message.id === greetingMessageId)
    if (greeting) greeting.content = greetingFor(professionalName.value)
  }

  function open(initialIntent?: string): void {
    isOpen.value = true
    if (initialIntent) {
      messages.value.push({ id: crypto.randomUUID(), role: 'assistant', content: initialIntent })
    }
  }

  function adoptConversationId(id: string): void {
    conversationId.value = id
    sessionStorage.setItem('conversation_id', id)
  }

  async function send(message: string): Promise<void> {
    const cleanMessage = message.trim()
    if (!cleanMessage || isSending.value) return

    messages.value.push({ id: crypto.randomUUID(), role: 'user', content: cleanMessage })
    isSending.value = true

    try {
      const response = await sendChatMessage({ conversationId: conversationId.value, message: cleanMessage })
      adoptConversationId(response.conversationId)
      messages.value.push({ id: crypto.randomUUID(), role: 'assistant', content: response.message })
    } catch {
      const directContact = professionalName.value || 'a profissional'
      messages.value.push({
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `O atendimento virtual está temporariamente indisponível. Você pode falar diretamente com ${directContact} pelo WhatsApp.`,
      })
    } finally {
      isSending.value = false
    }
  }

  async function resume(phone: string, managementCode: string): Promise<boolean> {
    if (isResuming.value) return false
    isResuming.value = true
    resumeError.value = ''

    try {
      const response = await resumeConversation({
        conversationId: conversationId.value,
        phone,
        managementCode,
      })
      adoptConversationId(response.conversationId)
      clientName.value = response.clientName
      continuityAuthenticated.value = true
      messages.value = response.messages.length
        ? response.messages.map((message) => ({
            id: message.id,
            role: message.role === 'USER' ? 'user' : 'assistant',
            content: message.content,
          }))
        : [{
            id: crypto.randomUUID(),
            role: 'assistant',
            content: `Pronto, ${response.clientName}. Sua identificação foi confirmada e podemos continuar por aqui.`,
          }]
      return true
    } catch {
      resumeError.value = 'Não foi possível confirmar os dados. Use o telefone e o código do seu agendamento mais recente.'
      return false
    } finally {
      isResuming.value = false
    }
  }

  return {
    isOpen,
    isSending,
    isResuming,
    continuityAuthenticated,
    resumeError,
    clientName,
    professionalName,
    messages,
    canSend,
    conversationId,
    open,
    send,
    resume,
    adoptConversationId,
    setProfessionalName,
  }
})
